import { useState, useEffect } from 'react';
import axios from 'axios';
import CreateSection from './CreateSection';
import PostsSection from './PostsSection';
import IdeasSection from './IdeasSection';
import AIMagicSection from './AIMagicSection';
import NewsSection from './NewsSection';
import AIAgentSection from './AIAgentSection';
import WebsiteSection from './WebsiteSection';
import SettingsSection from './SettingsSection';
import TemplatesSection from './TemplatesSection';
import ScheduleSection from './ScheduleSection';
import AnalyticsSection from './AnalyticsSection';
import AISEOSection from './AISEOSection';
import PlatformsSection from './PlatformsSection';
import PricingSection from './PricingSection';
import IndustrySwitcher from './IndustrySwitcher';
import AdminDashboard from './AdminDashboard';
import Sidebar from './Sidebar';
import BrandCreation from './BrandCreation';

const BACKEND_URL = window.location.origin || process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function DashboardNew({ user, onLogout, platforms }) {
  const [activeTab, setActiveTab] = useState('create');
  const [currentUser, setCurrentUser] = useState(user);
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    // Refresh user data to get latest industry info
    const fetchUser = async () => {
      try {
        const response = await axios.get(`${API}/users/${user.id}`);
        setCurrentUser(response.data);
      } catch (err) {
        console.error('Failed to fetch user:', err);
      }
    };
    fetchUser();
  }, [user.id]);

  useEffect(() => {
    // Show/hide scroll to top button
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleIndustryChange = async (industry) => {
    // Refresh user data after industry change
    try {
      const response = await axios.get(`${API}/users/${user.id}`);
      setCurrentUser(response.data);
    } catch (err) {
      console.error('Failed to refresh user:', err);
    }
  };

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const navTabs = [
    { id: 'create', label: 'Create', icon: '✨' },
    { id: 'ideas', label: 'Ideas', icon: '💡' },
    { id: 'aimagic', label: 'AI Magic', icon: '🪄' },
    { id: 'brandcreation', label: 'Brand Creation', icon: '🎨' },
    { id: 'news', label: 'News', icon: '📰' },
    { id: 'agent', label: 'AI Schedule', icon: '🤖', comingSoon: true },
    { id: 'website', label: 'Website Developer', icon: '🌐', comingSoon: true },
    { id: 'posts', label: 'Posts', icon: '📝' },
    { id: 'aiseo', label: 'AI SEO', icon: '🔍' },
    { id: 'schedule', label: 'Schedule', icon: '📅' },
    { id: 'analytics', label: 'Analytics', icon: '📊' },
    { id: 'platforms', label: 'Platforms', icon: '📱' },
    { id: 'settings', label: 'Settings', icon: '⚙️' },
    ...(currentUser.role === 'admin' || currentUser.email === 'aayazali@gmail.com' ? [{ id: 'admin', label: 'Admin', icon: '🛡️' }] : [])
  ];

  // Get the display industry (current_industry for admin, otherwise main industry)
  const displayIndustry = currentUser.current_industry || currentUser.industry;

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--primary-bg)' }}>
      {/* Sidebar Navigation */}
      <Sidebar
        navTabs={navTabs}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        user={currentUser}
      />

      {/* Main Content Area with Sidebar Offset */}
      <div className="lg:ml-64 transition-all duration-300">
        {/* Header */}
        <header className="backdrop-blur-md shadow-lg border-b sticky top-0 z-30" style={{ 
          backgroundColor: 'var(--secondary-bg)', 
          borderColor: 'var(--border-color)' 
        }}>
          <div className="px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex justify-between items-center">
              {/* Page Title & Industry */}
              <div className="flex items-center space-x-4">
                <div>
                  <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
                    {navTabs.find(t => t.id === activeTab)?.label || 'Dashboard'}
                  </h2>
                  <p className="text-xs accent-text">{displayIndustry}</p>
                </div>
              </div>

              {/* Right Side - User Info & Actions */}
              <div className="flex items-center space-x-3">
                {/* Admin Industry Switcher */}
                <IndustrySwitcher 
                  user={currentUser} 
                  onIndustryChange={handleIndustryChange}
                  compact={true}
                />
                
                {currentUser.role === 'admin' && (
                  <button className="hidden sm:block px-3 py-1.5 bg-green-600 text-white text-xs rounded-full font-medium hover:bg-green-700 transition">
                    Admin ∞
                  </button>
                )}

                <button className="hidden md:block px-3 py-1.5 accent-gradient text-white text-xs rounded-full font-medium transition">
                  {(currentUser.subscription_tier === 'enterprise' || currentUser.subscription_plan === 'enterprise') ? 'Enterprise' : 
                   (currentUser.subscription_tier === 'pro' || currentUser.subscription_plan === 'pro') ? 'Pro' :
                   (currentUser.subscription_tier === 'basic' || currentUser.subscription_plan === 'basic') ? 'Basic' : 'Free Plan'}
                </button>

                <button className="hidden lg:block px-3 py-1.5 border accent-text text-xs rounded-full font-medium transition" style={{ borderColor: 'var(--border-color)' }}>
                  🛟 Help
                </button>

                <button
                  onClick={onLogout}
                  data-testid="logout-button"
                  className="px-3 py-1.5 border text-xs rounded-full font-medium transition"
                  style={{ 
                    borderColor: 'var(--border-color)', 
                    color: 'var(--text-secondary)',
                    backgroundColor: 'transparent'
                  }}
                  onMouseEnter={(e) => e.target.style.backgroundColor = 'var(--hover-bg)'}
                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                >
                  <span className="hidden sm:inline">➜ Logout</span>
                  <span className="sm:hidden">↪</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="px-4 sm:px-6 lg:px-8 py-8 min-h-screen">
        {activeTab === 'create' && <CreateSection user={currentUser} platforms={platforms} />}
        {activeTab === 'ideas' && <IdeasSection user={currentUser} platforms={platforms} />}
        {activeTab === 'aimagic' && <AIMagicSection user={currentUser} />}
        {activeTab === 'brandcreation' && <BrandCreation user={currentUser} />}
        {activeTab === 'news' && <NewsSection user={currentUser} />}
        {activeTab === 'agent' && <AIAgentSection user={currentUser} />}
        {activeTab === 'website' && <WebsiteSection user={currentUser} />}
        {activeTab === 'posts' && <PostsSection user={currentUser} />}
        {activeTab === 'aiseo' && <AISEOSection user={currentUser} />}
        {activeTab === 'schedule' && <ScheduleSection user={currentUser} platforms={platforms} />}
        {activeTab === 'analytics' && <AnalyticsSection user={currentUser} />}
        {activeTab === 'platforms' && <PlatformsSection user={currentUser} />}
        {activeTab === 'settings' && <SettingsSection user={currentUser} onUserUpdate={handleIndustryChange} />}
        {activeTab === 'admin' && <AdminDashboard currentUser={currentUser} />}
      </main>

        {/* Scroll to Top Button */}
        {showScrollTop && (
          <button 
            onClick={scrollToTop}
            className="fixed bottom-8 right-8 w-12 h-12 accent-gradient rounded-full flex items-center justify-center text-white text-xl shadow-lg hover:shadow-xl transition glow-effect z-20"
            title="Scroll to top"
          >
            ⬆
          </button>
        )}
      </div>
    </div>
  );
}

export default DashboardNew;
